@if (indicators(); as metrics) {
  <div class="flex gap-4">
    <!--
      The count opens the row (LEN-2363): how many facilities before how much
      money, so the amount that follows reads as their size.
    -->
    @if (facilities() !== undefined) {
      <selectable-widget
        #swFac
        [exportMode]="exportMode()"
        [selected]="isWidgetSelected(swFac.nativeEl)"
        (widgetToggle)="
          widgetToggle.emit({ checked: $event, el: swFac.nativeEl })
        "
        class="card-grey flex h-36 grow flex-col gap-2 p-4">
        <div>
          <small class="font-normal text-surface-500">
            {{ 'Facilities' | uppercase }}
          </small>
          <h1 class="font-body font-bold">{{ facilities() }}</h1>
        </div>
      </selectable-widget>
    }

    <!--
      The lender's commitment heads the card when it was reported, with the
      portfolio's ceiling and the share it represents below. Otherwise the
      ceiling alone. "Total capital disbursed" named neither.
    -->
    <selectable-widget
      #sw3
      [exportMode]="exportMode()"
      [selected]="isWidgetSelected(sw3.nativeEl)"
      (widgetToggle)="widgetToggle.emit({ checked: $event, el: sw3.nativeEl })"
      class="card-grey flex min-h-36 grow flex-col gap-2 p-4">
      <div>
        <small class="font-normal text-surface-500">
          {{ amountTitle() }}
        </small>
        <h1 class="mb-2 font-body font-bold">
          <currency-amount
            [value]="
              showsCommitment()
                ? metrics.committed_amount!
                : metrics.total_credit_amount
            "
            [currency]="currency()" />
        </h1>
        @if (comparesToCeiling()) {
          <small class="text-surface-500">
            of
            <span class="font-bold">
              <currency-amount
                [value]="metrics.total_credit_amount"
                [currency]="currency()"
                [showCode]="false" />
            </span>
          </small>
          @if (sharePct() !== undefined) {
            <!--
              Off the "of $X" line on purpose (LEN-3334): the percentage
              divides only the facilities that have both amounts, so printed
              as "(Y%)" right after the portfolio's whole ceiling it showed an
              arithmetic that does not hold -- "$100 of $800 (25%)". The
              wording and the precision are the Portfolio tab's YOUR
              PARTICIPATION card's, which answers the same question.
            -->
            <small class="mt-1 block text-surface-500">
              <span class="font-bold">
                {{ sharePct() | number: '1.0-1' }}%
              </span>
              @if (shareExcluded()) {
                <!-- Some facility was left out of the quotient: saying "the
                     facilities you are in" would claim it measured all of
                     them (MR !1366 S3). -->
                <span i18n>
                  of the {{ shareCounted() }}
                  {{ shareCounted() === 1 ? 'facility' : 'facilities' }} with
                  both amounts
                </span>
              } @else {
                <span i18n>of the facilities you are in</span>
              }
            </small>
          }
          @if (trustsWithoutCommitment(); as missing) {
            <small class="mt-1 block text-surface-500">
              {{ missing }} {{ missing === 1 ? 'facility' : 'facilities' }}
              without a reported commitment
            </small>
          }
          @if (exceedsCeiling()) {
            <!-- Case 05: a contradiction, flagged in amber like the facility
                 detail's warnings (LEN-2365). -->
            <small
              class="mt-1 flex items-center gap-1 font-semibold text-amber-700"
              role="status">
              <ng-icon name="phosphorWarningCircle" size="14px" />
              {{ exceedsCeilingNote }}
            </small>
          }
        } @else if (ceilingNotReported()) {
          <small class="text-surface-500">{{ totalNotReported }}</small>
        } @else if (commitmentNotReported()) {
          <small class="text-surface-500">Commitment not reported</small>
        }
      </div>
    </selectable-widget>

    <selectable-widget
      #sw1
      [exportMode]="exportMode()"
      [selected]="isWidgetSelected(sw1.nativeEl)"
      (widgetToggle)="widgetToggle.emit({ checked: $event, el: sw1.nativeEl })"
      class="card-grey flex h-36 grow flex-col gap-2 p-4">
      <div>
        <small class="font-normal text-surface-500">
          {{ 'Portfolio Balance' | uppercase }}
        </small>
        <h1 class="mb-2 font-body font-bold">
          <currency-amount
            [value]="metrics.portfolio_balance.total"
            [currency]="currency()" />
        </h1>
        <small class="text-surface-500">
          Eligible
          <span class="font-bold">
            <currency-amount
              [value]="metrics.portfolio_balance.elegible"
              [currency]="currency()"
              [showCode]="false" />
          </span>
        </small>
        <small class="text-surface-500">
          Ineligible
          <span class="font-bold">
            <currency-amount
              [value]="metrics.portfolio_balance.inelegible"
              [currency]="currency()"
              [showCode]="false" />
          </span>
        </small>
      </div>
    </selectable-widget>

    <selectable-widget
      #sw2
      [exportMode]="exportMode()"
      [selected]="isWidgetSelected(sw2.nativeEl)"
      (widgetToggle)="widgetToggle.emit({ checked: $event, el: sw2.nativeEl })"
      class="card-grey flex h-36 grow flex-col gap-2 p-4">
      <div>
        <small class="font-normal text-surface-500">
          {{ 'Outstanding Debt' | uppercase }}
        </small>
        <h1 class="mb-2 font-body font-bold">
          <currency-amount
            [value]="metrics.outstanding_debt"
            [currency]="currency()" />
        </h1>
        <small class="text-surface-500">Unpaid capital</small>
      </div>
    </selectable-widget>

    @if (metrics.interest_rate) {
      <selectable-widget
        #sw4
        [exportMode]="exportMode()"
        [selected]="isWidgetSelected(sw4.nativeEl)"
        (widgetToggle)="
          widgetToggle.emit({ checked: $event, el: sw4.nativeEl })
        "
        class="card-grey flex h-36 grow flex-col gap-2 p-4">
        <div>
          <small class="font-normal text-surface-500">
            {{ 'Average interest rate' | uppercase }}
          </small>
          <h1 class="mb-2 font-body font-bold">
            {{ metrics.interest_rate * 100 | number: '1.2-2' }} %
          </h1>
          <small class="text-surface-500 underline"
            >Weighted average rate</small
          >
          @if (metrics.spread) {
            <small class="text-surface-500">
              Spread: <span class="font-bold">{{ metrics.spread }}% </span>
            </small>
          }
        </div>
      </selectable-widget>
    }
  </div>
}
